import { useState } from 'react';
import { authenticatedFetch } from '@/utils/api';
import { useLanguage } from '@/contexts';
import { LimitGuard } from '../LimitGuard';

interface CreateCollectionModalProps {
  onClose: () => void;
  onCreateSuccess: (collection: Record<string, unknown>) => void;
}

export default function CreateCollectionModal({ onClose, onCreateSuccess }: CreateCollectionModalProps) {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError(t("enterCollectionName"));
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await authenticatedFetch('/api/collections', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          is_private: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle limit exceeded error specifically
        if (response.status === 403 && (errorData.error?.code === 'LIMIT_EXCEEDED' || errorData.error?.code === 'FORBIDDEN')) {
          const errorMessage = errorData.error?.message || 
            errorData.error?.details?.[0] || 
            'You have reached your limit. Please upgrade your plan.';
          setError(errorMessage);
          // Dispatch event to refresh usage limits
          window.dispatchEvent(new CustomEvent('usageUpdated'));
          return;
        }
        
        // Handle both old and new error response formats
        const errorMessage = errorData.error?.message || errorData.error || errorData.message || 'Error creating collection';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const newCollection = data.data;
      
      // Dispatch custom event to trigger usage limits refetch
      window.dispatchEvent(new CustomEvent('usageUpdated'));
      
      onCreateSuccess(newCollection);
    } catch (error) {
      setError(error instanceof Error ? error.message : t("errorCreatingCollection"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-black bg-opacity-50 dark:bg-opacity-70 flex justify-center items-center p-4 z-50">
      <LimitGuard feature="collections">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:shadow-2xl w-full max-w-md transform transition-all">
          {/* Modal Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{t("createNewCollection")}</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              disabled={isLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Modal Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6">
              <div className="space-y-4">
                {/* Collection name input field */}
                <div>
                  <label htmlFor="collection-name" className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    {t("collectionName")}
                  </label>
                  <input 
                    type="text" 
                    id="collection-name" 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("collectionInputPlaceholder")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-transparent transition-colors duration-200"
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Error message */}
                {error && (
                  <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                )}

                {/* Primary action button */}
                <button 
                  type="submit"
                  className="w-full bg-brand-dark-blue dark:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? t("creating") : t("createCollection")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </LimitGuard>
    </div>
  );
}
